/**
 * The demo script: every step of the click-through demo, in order. Each step names the app, the
 * page, the account it runs as, what to say and what to do. Ids come from the server's demo
 * story (GET /api/demo/story, apps/adthlete-backend/services/demo/demoStory.ts), so the steps
 * work after any reset.
 *
 * To change the demo, edit this list: add, remove or reorder steps; numbers follow the order.
 */

export type DemoApp = 'athlete' | 'marketer'

export const DEMO_APPS: Record<DemoApp, string> = {
  athlete: 'athlete app',
  marketer: 'brand app',
}

type Account = { email: string; name: string; userType: string }

export type DemoStory = {
  ready: boolean
  error?: string
  password: string
  accounts: Record<'newAthlete' | 'newBrand' | 'athlete' | 'brand', Account>
  ids: {
    emma: string
    applyWave: string
    application: string | null
    offerDeal: string
    offerWave: string
    reviewDeal: string
    reviewWave: string
    reviewMilestone: string
    codesDeal: string
    codesWave: string
    paidDeal: string
    paidWave: string
  }
}

export type DemoStep = {
  n: number
  act: string
  app: DemoApp
  path: string
  account: Account & { password: string }
  title: string
  /** What to tell the room */
  say: string
  /** What to click or show */
  do: string
}

type Draft = Omit<DemoStep, 'n' | 'account'> & { as: keyof DemoStory['accounts'] }

export function buildDemoSteps(story: DemoStory): DemoStep[] {
  if (!story?.ready) return []
  const { ids } = story
  const drafts: Draft[] = [
    // ── 1. An athlete joins ─────────────────────────────────────────────────
    {
      act: 'Athlete onboarding',
      app: 'athlete',
      as: 'newAthlete',
      path: '/welcome',
      title: 'A new athlete is in, straight away',
      say: 'Maya just joined from athios.ai. A phone check, a password, her birthday and the terms, and she is in: any offer waiting for her comes first. No quests, no score.',
      do: 'Show the four steps of her first month with the Athios Lab: strategy call, shoot days, content calendar, Content Shop.',
    },
    {
      act: 'Athlete onboarding',
      app: 'athlete',
      as: 'newAthlete',
      path: '/brand-view',
      title: 'Her Content Shop, as brands see it',
      say: 'The Lab builds it with her, from the manager app. Anything she wants to add herself is one tap away, and none of it stands between her and her offers.',
      do: 'Scroll the shop and its set-up checklist.',
    },
    {
      act: 'Athlete onboarding',
      app: 'athlete',
      as: 'newAthlete',
      path: '/setup/about',
      title: 'Profile: photo, bio, sport, school',
      say: 'The basics brands filter on. Short and on the phone.',
      do: 'Fill in a sentence of bio and pick a sport, or just scroll it.',
    },
    {
      act: 'Athlete onboarding',
      app: 'athlete',
      as: 'newAthlete',
      path: '/setup/themes',
      title: 'Top 5 content themes',
      say: 'What she posts again and again, ranked. Brands search and book by these, and her content calendar plans around them.',
      do: 'Add five themes and drag one to the top.',
    },
    {
      act: 'Athlete onboarding',
      app: 'athlete',
      as: 'newAthlete',
      path: '/setup/rates',
      title: 'Rate card in one screen',
      say: "Suggested starting prices, which she can keep or change, plus ad licensing. Brands book at these prices, so most deals don't need back-and-forth.",
      do: 'Show the suggested ranges and the licensing fees.',
    },
    {
      act: 'Athlete onboarding',
      app: 'athlete',
      as: 'newAthlete',
      path: '/setup/publish',
      title: 'Publish and go live to brands',
      say: 'One tap and her page is in the brand marketplace.',
      do: 'Show the publish screen.',
    },

    // ── 2. A brand joins ────────────────────────────────────────────────────
    {
      act: 'Brand onboarding',
      app: 'marketer',
      as: 'newBrand',
      path: '/setup?first=1',
      title: 'A new brand sets up',
      say: 'Sam from Peak Hydration signs up. Setup asks for the brand and who they want to reach: sports, schools, states and level.',
      do: 'Walk the setup screens.',
    },
    {
      act: 'Brand onboarding',
      app: 'marketer',
      as: 'newBrand',
      path: '/',
      title: 'The brand home',
      say: 'From here a brand posts an opportunity, shops athletes’ content, or reviews who applied.',
      do: 'Show the dashboard and the menu.',
    },

    // ── 3. An athlete pitches a brand ───────────────────────────────────────
    {
      act: 'Athlete pitches',
      app: 'athlete',
      as: 'athlete',
      path: '/opportunities/all',
      title: 'Opportunities matched to the athlete',
      say: 'Emma is an established athlete. Her marketplace shows paid content, gifting and appearances that fit her sport and school, best fit first.',
      do: 'Scroll the opportunities, then open FitGear’s "Try the new training line".',
    },
    {
      act: 'Athlete pitches',
      app: 'athlete',
      as: 'athlete',
      path: `/opportunity/${ids.applyWave}`,
      title: 'She submits herself for review',
      say: 'Emma reads the brief (a free FitGear training kit for a post) and pitches herself in one message. On paid opportunities she sends her rate card price or her own ask.',
      do: 'Write a line and tap Send. It goes to the brand to review.',
    },

    // ── 4. The brand reviews ────────────────────────────────────────────────
    {
      act: 'Brand reviews',
      app: 'marketer',
      as: 'brand',
      path: '/submissions',
      title: 'Submissions inbox',
      say: 'Jordan at Sports Marketing Agency sees everyone who applied: the pitch, the price, and a link to the athlete’s Content Shop with level and score.',
      do: 'Open Emma’s pitch for "Try the new training line" and Accept it. The deal starts on both sides.',
    },

    // ── 5. The brand shops ──────────────────────────────────────────────────
    {
      act: 'Brand shops',
      app: 'marketer',
      as: 'brand',
      path: '/marketplace',
      title: 'Marketplace: shop athlete content',
      say: 'Brands can also go shopping. Each card leads with the athlete’s content and a from-price. Filter by content, sport, state and audience.',
      do: 'Filter, then open Emma Davis.',
    },
    {
      act: 'Brand shops',
      app: 'marketer',
      as: 'brand',
      path: `/athlete/${ids.emma}`,
      title: "The athlete's Content Shop",
      say: 'Her content first, then each content theme as something to buy: the example post, the formats and the price. Score, audience and track record back it up.',
      do: 'Pick a format on a theme and press Book this: the offer opens filled in.',
    },

    // ── 6. A deal gets made ─────────────────────────────────────────────────
    {
      act: 'Deal-making',
      app: 'athlete',
      as: 'athlete',
      path: `/deals/${ids.offerDeal}`,
      title: 'The athlete answers an offer',
      say: 'FitGear offered $1,500 for a Reel and a TikTok, plus 90 days of paid social. Emma can accept, counter or pass in one screen.',
      do: 'Counter at a higher price, or Accept.',
    },
    {
      act: 'Deal-making',
      app: 'marketer',
      as: 'brand',
      path: `/collaborations/${ids.offerDeal}`,
      title: 'The brand sees her answer',
      say: 'Same deal, brand side. A counter lands here to accept or answer; an acceptance moves it straight to the work.',
      do: 'Accept the counter, if she countered.',
    },

    // ── 7. The work ─────────────────────────────────────────────────────────
    {
      act: 'Project management',
      app: 'athlete',
      as: 'athlete',
      path: `/deals/${ids.reviewDeal}`,
      title: 'One deal screen from offer to paid',
      say: 'Sent, Agreed, Do the work, Complete, Paid. One next step at a time. Here her video for Spring Training Launch is with the brand.',
      do: 'Show the stage bar and the submitted video.',
    },
    {
      act: 'Project management',
      app: 'marketer',
      as: 'brand',
      path: `/campaign/${ids.reviewWave}/athlete/${ids.reviewDeal}/milestone/${ids.reviewMilestone}/review`,
      title: 'The brand reviews the content',
      say: 'Timestamped notes on the video and caption edits, or approve. Compliance (NIL Go) is handled on deals that need it.',
      do: 'Leave a note at a timestamp, then Approve.',
    },

    // ── 8. Live and licensed ────────────────────────────────────────────────
    {
      act: 'Live and ad codes',
      app: 'athlete',
      as: 'athlete',
      path: `/deals/${ids.codesDeal}`,
      title: 'Posted, now the ad codes',
      say: "Summer Hydration Reels are live. Because the brand bought paid social rights, the last step is the partnership ad code for Instagram and TikTok's confirmation.",
      do: 'Enter an Instagram ad code and confirm TikTok.',
    },
    {
      act: 'Live and ad codes',
      app: 'marketer',
      as: 'brand',
      path: `/collaborations/${ids.codesDeal}`,
      title: 'The brand gets the codes',
      say: 'The codes arrive in the brand workspace, ready for their ads team.',
      do: 'Show the post links and the ad codes.',
    },

    // ── 9. Done ─────────────────────────────────────────────────────────────
    {
      act: 'Complete and paid',
      app: 'marketer',
      as: 'brand',
      path: `/collaborations/${ids.paidDeal}`,
      title: 'Complete and paid',
      say: 'Recovery Series: delivered, licensed, paid. The brand has the results, the invoice and the content in one place.',
      do: 'Show the results and the invoice.',
    },
    {
      act: 'Complete and paid',
      app: 'athlete',
      as: 'athlete',
      path: '/earnings',
      title: 'The athlete gets paid',
      say: 'Emma sees what she earned and what is on the way.',
      do: 'Show earnings, then her home screen.',
    },
  ]
  return drafts.map(({ as, ...d }, i) => ({
    ...d,
    n: i + 1,
    account: { ...story.accounts[as], password: story.password },
  }))
}
